import { ExternalLink } from "lucide-react";
import { Chip, DataTable, Meter } from "./DataTable";
import { StatTile, rateTone } from "./Evidence";
import { inr, num, pct } from "../lib/format";

/** One state, board-ready: what we fund, how it lands, and where the state stands on need. */
export const StateBriefCard = ({ brief, compact = false }) => {
  const p = brief.portfolio;
  return (
    <section className="rounded-xl border border-stone-200 bg-white p-4 space-y-3.5"
             data-testid={`state-brief-${brief.state}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="text-[16px] font-semibold text-stone-900 font-display">
          {brief.state}
          <span className="text-stone-600 font-normal text-[13px]"> · {brief.state_abbr}</span>
        </h3>
        <a href={`/brief?state=${encodeURIComponent(brief.state)}`}
           className="text-[12px] font-medium text-[#A01236] inline-flex items-center gap-1 hover:underline"
           data-testid={`open-brief-${brief.state}`}>
          Open the printable brief <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <StatTile label="Committed" tone="brand" value={inr(p.committed_inr)}
                  sub={`${num(p.grants)} grants · all themes`} />
        <StatTile label="Utilisation" tone={rateTone(brief.utilisation_rate_pct)}
                  value={pct(brief.utilisation_rate_pct)}
                  sub={`${num(brief.closed_grants)} ended grants`} />
        <StatTile label="Districts reached" tone="sky" value={num(p.districts_we_fund)}
                  sub={p.aspirational_districts_we_fund?.length
                    ? `${p.aspirational_districts_we_fund.length} aspirational`
                    : "none aspirational"} />
        <StatTile label="Partners" tone="sky" value={num(brief.partner_count)}
                  sub={`${inr(brief.in_flight_inr)} still in flight`} />
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid={`brief-themes-${brief.state}`}>
        {Object.entries(p.committed_by_theme_inr || {}).map(([t, v]) => (
          <span key={t}
                className="inline-flex items-center gap-1.5 text-[12px] text-stone-700 bg-stone-50 border border-stone-200 rounded-full px-2.5 py-0.5">
            {t}<span className="num font-semibold text-stone-900">{inr(v)}</span>
          </span>
        ))}
      </div>

      {p.aspirational_districts_we_fund?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-[12px] text-stone-600">Aspirational districts funded:</span>
          {p.aspirational_districts_we_fund.map((d) => (
            <Chip key={d} label={d} tone="review" />
          ))}
        </div>
      )}

      <IndicatorStanding rows={brief.indicator_standing} limit={compact ? 5 : 13}
                         state={brief.state} />

      {!compact && brief.top_partners?.length > 0 && (
        <div>
          <Sub>Largest partners here</Sub>
          <ul className="mt-1.5 grid sm:grid-cols-2 gap-x-6 gap-y-1">
            {brief.top_partners.map((x) => (
              <li key={x.partner} className="text-[13px] text-stone-700 flex justify-between gap-3">
                <span className="truncate">{x.partner}</span>
                <span className="num text-stone-900 shrink-0">{inr(x.committed_inr)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {brief.peer_csr_spend_inr && (
        <p className="text-[13px] text-stone-600 leading-relaxed">
          All companies together filed <span className="num font-semibold text-stone-800">
          {inr(brief.peer_csr_spend_inr)}</span> of CSR in {brief.state} in {brief.peer_fy}{" "}
          (MCA filings, cells of {brief.peer_companies_in_largest_cell} or more companies).
        </p>
      )}
    </section>
  );
};

const IndicatorStanding = ({ rows, limit = 5, state }) => {
  if (!rows?.length) return null;
  return (
    <div data-testid={`indicator-standing-${state}`}>
      <Sub>Where the state stands — published indicators, worst first</Sub>
      <p className="text-[12px] text-stone-600 mt-1 mb-2 leading-relaxed">
        Each row compares {state} with the median of the states that report that indicator, so the
        denominator changes by indicator. Rank is worst first: rank 1 is the worst-off state.
      </p>
      <ul className="divide-y divide-stone-100 border-y border-stone-100">
        {rows.slice(0, limit).map((r) => {
          const worse = r.is_worse_than_median;
          const tone = worse === null ? "text-stone-600"
            : worse ? "text-rose-700" : "text-emerald-700";
          return (
            <li key={r.indicator}
                className="py-1.5 text-[13px] text-stone-700 flex items-baseline gap-3"
                title={`${r.direction_note}. Reference period ${r.reference_period}. `
                  + `Median of reporting states ${r.national_median} ${r.unit}. `
                  + `Worst: ${r.worst_state} at ${r.worst_value}.`}>
              <span className="flex-1 min-w-0">
                <span className="truncate">{r.label}</span>
                <span className="text-stone-600"> · {r.unit}</span>
              </span>
              <span className="shrink-0 w-[4.5rem] text-right num font-semibold text-stone-900">
                {r.value}
              </span>
              <span className={`shrink-0 w-[11rem] text-right num ${tone}`}>
                {worse === null ? "at median" : (
                  <>
                    {r.gap_vs_median > 0 ? "▲" : "▼"} {r.gap_abs} {worse ? "worse" : "better"}
                    <span className="text-stone-500"> vs {r.national_median}</span>
                  </>
                )}
              </span>
              <span className="shrink-0 w-[6.5rem] text-right num text-stone-600">
                rank {r.rank_worst_first}/{r.states_reporting}
                {r.top_quartile_worst && (
                  <span className="text-rose-700 font-semibold" title="In the worst quartile of reporting states"> !</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** One district: our money, how it lands, who delivers, and the public CSR filed alongside. */
export const DistrictProfile = ({ profile }) => (
  <div className="space-y-4" data-testid="district-profile">
    <div className="flex items-baseline gap-2.5 flex-wrap">
      <h3 className="text-[17px] font-semibold text-stone-900 font-display">{profile.label}</h3>
      {profile.adp?.adp_status === "aspirational" && (
        <Chip label="aspirational district" tone="review" title={profile.adp.adp_source} />
      )}
      {profile.adp?.adp_status === "not_in_published_list" && (
        <Chip label="not in published list" tone="muted" title={profile.adp.adp_caveat} />
      )}
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
      <StatTile label="Committed here" tone="brand" value={inr(profile.committed_inr)}
                sub={`${num(profile.grants)} grants · apportioned evenly`} />
      <StatTile label="Utilisation" tone={rateTone(profile.utilisation_rate_pct)}
                value={pct(profile.utilisation_rate_pct)}
                sub={`${num(profile.closed_grants)} ended grants`} />
      <StatTile label="Stalled" tone={profile.stalled_inr > 0 ? "amber" : "neutral"}
                value={inr(profile.stalled_inr)}
                sub={`${inr(profile.in_flight_inr)} still in flight`} />
      <StatTile label="₹ per beneficiary" tone="neutral"
                value={profile.cost_per_beneficiary_internal
                  ? inr(profile.cost_per_beneficiary_internal) : "withheld"}
                title={profile.cost_per_beneficiary_note || undefined}
                sub={`${num(profile.beneficiaries_declared)} declared · mGrant-internal only`} />
    </div>

    <div>
      <Sub>Committed by theme — {Object.keys(profile.committed_by_fy_inr || {})[0]} to{" "}
        {Object.keys(profile.committed_by_fy_inr || {}).slice(-1)[0]}, all years together</Sub>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {Object.entries(profile.committed_by_theme_inr || {}).map(([t, v]) => (
          <span key={t}
                className="inline-flex items-center gap-1.5 text-[12px] text-stone-700 bg-stone-50 border border-stone-200 rounded-full px-2.5 py-0.5">
            {t}<span className="num font-semibold text-stone-900">{inr(v)}</span>
          </span>
        ))}
      </div>
      <p className="text-[12px] text-stone-600 mt-1.5 leading-relaxed"
         data-testid="district-committed-by-fy">
        Commitment across the whole grant period, not annual spend. By year:{" "}
        {Object.entries(profile.committed_by_fy_inr || {})
          .map(([f, v]) => `${f} ${inr(v)}`).join(" · ")}.
      </p>
    </div>

    {profile.public_csr && (
      <div data-testid="district-public-csr" className="space-y-2.5">
        <Sub>Everyone else funding this district</Sub>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          <StatTile label="All companies filed here" tone="sky"
                    value={inr(profile.public_csr.spent_inr)}
                    sub={`${profile.public_csr.period_label} · ${num(profile.public_csr.filed_rows)} filed rows`} />
          <StatTile label="Their outlay utilisation" tone="neutral"
                    value={profile.public_csr.utilisation_rate_pct != null
                      ? pct(profile.public_csr.utilisation_rate_pct) : "withheld"}
                    title={profile.public_csr.outlay_rows_excluded
                      ? `${profile.public_csr.outlay_rows_excluded} filed rows here carry an outlay over 100x their own spend — a unit error at source, excluded from this rate.`
                      : undefined}
                    sub={`against ${inr(profile.public_csr.outlay_inr)} of filed outlay`} />
          <StatTile label="Share of state CSR" tone="amber"
                    value={pct(profile.public_csr.share_of_state_public_pct)}
                    sub={`rank ${num(profile.public_csr.rank_in_state)} of ${num(profile.public_csr.districts_in_state_filing)} districts filing`} />
          <StatTile label="Bajaj Finserv committed" tone="brand"
                    value={inr(profile.committed_inr)}
                    sub="FY-2022 onward · different years" />
        </div>
        <DataTable
          testId="district-public-theme-table"
          minWidth="min-w-[22rem]"
          rows={profile.public_csr.by_theme}
          defaultSort={{ key: "public_spent_inr", order: "desc" }}
          columns={[
            { key: "theme_label", label: "Theme", sortValue: (r) => r.theme_label,
              render: (r) => <span className="font-medium text-stone-900">{r.theme_label}</span> },
            { key: "public_spent_inr", label: "All companies filed", align: "right",
              headerTitle: "MCA district-sector aggregate, FY2014-15 to FY2020-21. No company "
                + "names in the file, so the amount is citable and the filers are not.",
              className: "whitespace-nowrap", sortValue: (r) => r.public_spent_inr,
              render: (r) => r.public_spent_inr
                ? inr(r.public_spent_inr)
                : <span className="text-stone-500">—</span> },
            { key: "bajaj_committed_inr", label: "Bajaj Finserv committed", align: "right",
              headerTitle: "mGrant committed value, FY-2022 onward. A different measure over "
                + "different years — never differenced against the filed column.",
              className: "whitespace-nowrap", sortValue: (r) => r.bajaj_committed_inr,
              render: (r) => r.bajaj_committed_inr
                ? inr(r.bajaj_committed_inr)
                : <Chip label="we fund nothing here" tone="review" /> },
          ]}
        />
        <p className="text-[12px] text-stone-600 leading-relaxed">
          {profile.public_csr.not_comparable_reason} {profile.public_csr.no_company_names_reason}
        </p>
      </div>
    )}

    <div>
      <Sub>Need — published state indicators, not district</Sub>
      <p className="text-[12px] text-stone-600 mt-1 mb-1.5 leading-relaxed">
        {profile.state_indicator_note}
      </p>
      <IndicatorStanding rows={profile.state_indicator_standing} limit={6}
                         state={profile.state} />
    </div>

    <div>
      <Sub>Partners delivering here</Sub>
      <ul className="mt-1.5 grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {profile.partners.map((x) => (
          <li key={x.partner} className="text-[13px] text-stone-700 flex justify-between gap-3">
            <span className="truncate">{x.partner}</span>
            <span className="num text-stone-900 shrink-0">{inr(x.committed_inr)}</span>
          </li>
        ))}
      </ul>
    </div>

    <div>
      <Sub>Every grant recorded here</Sub>
      <div className="mt-2">
        <DataTable
          testId="district-grants-table"
          maxHeight="max-h-[24rem]"
          defaultSort={{ key: "committed_in_district", order: "desc" }}
          rows={profile.grant_rows}
          columns={[
            { key: "grant_name", label: "Grant", sortValue: (r) => r.grant_name,
              className: "min-w-[18rem]",
              render: (r) => (
                <div className="max-w-md">
                  <div className="font-medium text-stone-900">{r.grant_name}</div>
                  <div className="text-[12px] text-stone-600">
                    {r.partner} · {r.theme_label} · {r.fy}
                    {r.districts_split_across > 1 &&
                      ` · split across ${r.districts_split_across} districts`}
                  </div>
                </div>
              )},
            { key: "committed_in_district", label: "In district", align: "right",
              sortValue: (r) => r.committed_in_district,
              render: (r) => (
                <span title={`${inr(r.committed_total)} committed in total, apportioned across ${r.districts_split_across} district(s).`}>
                  {inr(r.committed_in_district)}
                </span>
              )},
            { key: "utilisation_rate_pct", label: "Utilisation", align: "right",
              sortValue: (r) => r.utilisation_rate_pct,
              render: (r) => r.in_flight
                ? <span className="text-stone-500" title="Still inside its period — utilisation is not judged yet.">in flight</span>
                : <Meter value={r.utilisation_rate_pct} /> },
            { key: "grant_status", label: "Status", align: "right",
              sortValue: (r) => r.grant_status,
              render: (r) => <Chip label={r.grant_status || "—"} tone="muted" /> },
          ]}
        />
      </div>
    </div>
  </div>
);

const Sub = ({ children }) => (
  <div className="text-[13px] font-semibold text-stone-800">{children}</div>
);
